import React, {useState} from 'react'
import {Box, Typography} from '@mui/material'
import ActionIconButton from 'src/components-new/primitives/ActionIconButton'
import AppDialog from 'src/components-new/feedback/AppDialog'
import Button from 'src/components-new/primitives/Button'
import InlineMessage from 'src/components-new/feedback/InlineMessage'
import TextFieldNew from 'src/components-new/inputs/TextFieldNew'
import MultiSelectNew from 'src/components-new/inputs/MultiSelectNew'
import {NEUTRAL_SCALE} from 'src/theme-new/tokens'
import apiClient from 'src/services/apiClient'

interface CategoryOption {
  value: string
  label: string
}

interface FileItem {
  file: File
  name: string
  categories: string[]
}

interface UploaderDialogNewProps {
  open: boolean
  onClose: () => void
  onUploaded: () => void
  categoryOptions: CategoryOption[]
}

export default function UploaderDialogNew({
  open,
  onClose,
  onUploaded,
  categoryOptions,
}: UploaderDialogNewProps): JSX.Element {
  const [files, setFiles] = useState<FileItem[]>([])
  const [uploading, setUploading] = useState(false)
  const [errorMsg, setErrorMsg] = useState<string | null>(null)

  const handleFilesAdd = (fileList: FileList | null) => {
    if (!fileList) return
    const newItems: FileItem[] = Array.from(fileList).map((f) => ({
      file: f,
      name: f.name,
      categories: [],
    }))
    setFiles((prev) => [...prev, ...newItems])
  }

  const handleRemove = (index: number) => {
    setFiles((prev) => prev.filter((_, i) => i !== index))
  }

  const handleUpdateName = (index: number, value: string) => {
    setFiles((prev) =>
      prev.map((item, i) => (i === index ? {...item, name: value} : item))
    )
  }

  const handleUpdateCategories = (index: number, value: string[]) => {
    setFiles((prev) =>
      prev.map((item, i) => (i === index ? {...item, categories: value} : item))
    )
  }

  const handleUpload = async () => {
    setUploading(true)
    setErrorMsg(null)
    try {
      for (const item of files) {
        const formData = new FormData()
        item.categories.forEach((c) => formData.append('category', c))
        formData.append('name', item.name || item.file.name)
        formData.append('file', item.file, item.file.name)
        await apiClient.postDocuments(formData, () => {})
      }
      setFiles([])
      onUploaded()
    } catch {
      setErrorMsg('Errore nel caricamento del documento.')
    } finally {
      setUploading(false)
    }
  }

  const handleClose = () => {
    if (!uploading) {
      setFiles([])
      setErrorMsg(null)
      onClose()
    }
  }

  const hasInvalidFiles = files.some((f) => !f.categories.length)

  const actions = (
    <Box sx={{display: 'flex', gap: 1}}>
      <Button variant="secondary" onClick={handleClose} disabled={uploading}>
        Annulla
      </Button>
      <Button
        variant="primary"
        onClick={handleUpload}
        loading={uploading}
        disabled={!files.length || hasInvalidFiles}
      >
        Carica tutto
      </Button>
    </Box>
  )

  return (
    <AppDialog
      open={open}
      onClose={handleClose}
      title="Carica documento"
      maxWidth="md"
      actions={actions}
    >
      <Box sx={{display: 'flex', flexDirection: 'column', gap: 2, pt: 1}}>
        {errorMsg && (
          <InlineMessage severity="error" onClose={() => setErrorMsg(null)}>
            {errorMsg}
          </InlineMessage>
        )}

        <Box
          component="label"
          onDragOver={(e: React.DragEvent) => { e.preventDefault(); e.stopPropagation() }}
          onDrop={(e: React.DragEvent) => {
            e.preventDefault()
            e.stopPropagation()
            handleFilesAdd(e.dataTransfer.files)
          }}
          sx={{
            display: 'block',
            border: `2px dashed ${NEUTRAL_SCALE[200]}`,
            borderRadius: '8px',
            p: 3,
            textAlign: 'center',
            cursor: 'pointer',
            backgroundColor: NEUTRAL_SCALE[50],
            '&:hover': {borderColor: NEUTRAL_SCALE[300], backgroundColor: NEUTRAL_SCALE[100]},
          }}
        >
          <input
            type="file"
            multiple
            hidden
            onChange={(e) => handleFilesAdd(e.target.files)}
          />
          <Typography variant="body2" color="text.secondary">
            Trascina i file qui o clicca per selezionare
          </Typography>
        </Box>

        {files.map((item, index) => (
          <Box
            key={index}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              gap: 1.5,
              p: 2,
              border: `1px solid ${NEUTRAL_SCALE[200]}`,
              borderRadius: '8px',
              backgroundColor: '#fff',
            }}
          >
            <Box sx={{display: 'flex', alignItems: 'center', justifyContent: 'space-between'}}>
              <Typography variant="body2" fontWeight={500}>
                {item.file.name}
                <Typography component="span" variant="caption" sx={{ml: 1, color: 'text.secondary'}}>
                  {`(${(item.file.size / 1024 ** 2).toFixed(2)} MB)`}
                </Typography>
              </Typography>
              <ActionIconButton variant="delete" onClick={() => handleRemove(index)} />
            </Box>
            <MultiSelectNew
              label="Categoria"
              value={item.categories}
              onChange={(v) => handleUpdateCategories(index, v)}
              options={categoryOptions}
              required
              fullWidth
            />
            <TextFieldNew
              label="Nome"
              value={item.name}
              onChange={(v) => handleUpdateName(index, v)}
              fullWidth
            />
          </Box>
        ))}
      </Box>
    </AppDialog>
  )
}
